import React, { useMemo } from "react";
import {
  Box,
  Button,
  HStack,
  IconButton,
  Select,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useCustomTheme } from "../../contexts/CustomThemeContext"; // ✅ adjust path to your project

type AppPaginationProps = {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showSummary?: boolean;
};

const AppPagination: React.FC<AppPaginationProps> = ({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50, 100],
  showPageSizeSelector = true,
  showSummary = true,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  // ✅ Theme support
  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const brand = theme.brand || {
    electricBlue: "#27b7ff",
    deepSlate: "#2a2c3b",
    deepInk: "#1a1c2f",
    slate2: "#2e3143",
    softLight: "#eaebf0",
    violet: "#b05cff",
    indigo: "#6f7cff",
  };

  const gradients = theme.gradients || {
    primaryButton: `linear-gradient(135deg, ${brand.electricBlue} 0%, #49d1ff 100%)`,
    primaryButtonHover: `linear-gradient(135deg, #1eaef3 0%, #2cc2ff 100%)`,
    shellBg: theme.surface,
  };

  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const borderColor = theme.border;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const softShadow = theme.shadowSm;

  const safePageSize = Math.max(1, Number(pageSize) || 1);
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));
  const safeCurrentPage = Math.min(
    Math.max(Number(currentPage) || 1, 1),
    totalPages,
  );

  const { startIndex, endIndex } = useMemo(() => {
    if (totalItems === 0) return { startIndex: 0, endIndex: 0 };
    const start = (safeCurrentPage - 1) * safePageSize + 1;
    const end = Math.min(safeCurrentPage * safePageSize, totalItems);
    return { startIndex: start, endIndex: end };
  }, [safeCurrentPage, safePageSize, totalItems]);

  const pageNumbers = useMemo(() => {
    const pages: (number | "...")[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    const left = Math.max(2, safeCurrentPage - 1);
    const right = Math.min(totalPages - 1, safeCurrentPage + 1);

    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    pages.push(totalPages);

    return pages;
  }, [safeCurrentPage, totalPages]);

  const goToPage = (page: number) => {
    const nextPage = Math.min(Math.max(Number(page) || 1, 1), totalPages);

    if (nextPage === safeCurrentPage) return;

    onPageChange(nextPage);
  };

  const handlePageSizeChange = (value: string) => {
    const nextSize = Math.max(1, Number(value) || safePageSize);

    if (nextSize === safePageSize) return;

    onPageSizeChange?.(nextSize);
  };

  const navBtnStyles = {
    size: "sm" as const,
    borderRadius: "12px",
    border: `1px solid ${borderColor}`,
    bg: isDark ? "rgba(255,255,255,0.03)" : cardBg,
    color: textColor,
    boxShadow: softShadow,
    _hover: {
      bg: isDark ? "rgba(255,255,255,0.06)" : "white",
      borderColor: brand.electricBlue,
      transform: "translateY(-1px)",
    },
    _active: {
      transform: "translateY(0)",
    },
    _disabled: {
      opacity: 0.45,
      cursor: "not-allowed",
    },
    transition: "all 0.2s ease",
  };

  const pageBtnBase = {
    size: "sm" as const,
    minW: "36px",
    borderRadius: "12px",
    fontWeight: 700,
    boxShadow: softShadow,
    transition: "all 0.2s ease",
  };

  return (
    <Box
      mt={4}
      p={{ base: 3, md: 4 }}
      borderRadius="16px"
      border={`1px solid ${borderColor}`}
      bg={isDark ? "rgba(255,255,255,0.02)" : softBg}
      boxShadow={softShadow}
      display="flex"
      flexDirection={{ base: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ base: "stretch", md: "center" }}
      gap={3}
    >
      <HStack spacing={3} flexWrap="wrap" align="center">
        {showSummary && (
          <Text fontSize="sm" color={mutedText} fontWeight="500">
            Showing {startIndex}-{endIndex} of {totalItems}
          </Text>
        )}

        {showPageSizeSelector && onPageSizeChange && (
          <HStack spacing={2}>
            <Text fontSize="sm" color={mutedText} fontWeight="500">
              Rows:
            </Text>
            <Select
              size="sm"
              w="96px"
              value={safePageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              borderRadius="12px"
              borderColor={borderColor}
              bg={cardBg}
              color={textColor}
              fontWeight="600"
              _hover={{ borderColor: brand.electricBlue }}
              _focus={{
                borderColor: brand.electricBlue,
                boxShadow: `0 0 0 1px ${brand.electricBlue}`,
              }}
              sx={{
                option: {
                  background: isDark ? "#1f2430" : "#ffffff",
                  color: isDark ? "#f7fafc" : "#1a202c",
                },
              }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </HStack>
        )}
      </HStack>

      <HStack
        spacing={1.5}
        justify={{ base: "space-between", md: "flex-end" }}
        flexWrap="wrap"
      >
        <IconButton
          type="button"
          aria-label="Previous page"
          icon={<ChevronLeftIcon />}
          onClick={() => goToPage(safeCurrentPage - 1)}
          isDisabled={safeCurrentPage <= 1}
          {...navBtnStyles}
        />

        {!isMobile &&
          pageNumbers.map((p, idx) =>
            p === "..." ? (
              <Text
                key={`ellipsis-${idx}`}
                px={2}
                fontSize="sm"
                color={mutedText}
                fontWeight="600"
              >
                ...
              </Text>
            ) : (
              <Button
                type="button"
                key={p}
                onClick={() => goToPage(p)}
                {...pageBtnBase}
                border={
                  p === safeCurrentPage
                    ? "1px solid transparent"
                    : `1px solid ${borderColor}`
                }
                bg={
                  p === safeCurrentPage
                    ? gradients.primaryButton
                    : isDark
                      ? "rgba(255,255,255,0.03)"
                      : cardBg
                }
                color={p === safeCurrentPage ? "white" : textColor}
                _hover={
                  p === safeCurrentPage
                    ? {
                        bg:
                          gradients.primaryButtonHover ||
                          gradients.primaryButton,
                        transform: "translateY(-1px)",
                      }
                    : {
                        bg: isDark ? "rgba(255,255,255,0.06)" : "white",
                        borderColor: brand.electricBlue,
                        transform: "translateY(-1px)",
                      }
                }
                _active={{ transform: "translateY(0)" }}
              >
                {p}
              </Button>
            ),
          )}

        {isMobile && (
          <Box
            px={3}
            py={1.5}
            borderRadius="12px"
            border={`1px solid ${borderColor}`}
            bg={cardBg}
            boxShadow={softShadow}
          >
            <Text fontSize="sm" color={textColor} fontWeight="700">
              {safeCurrentPage} / {totalPages}
            </Text>
          </Box>
        )}

        <IconButton
          type="button"
          aria-label="Next page"
          icon={<ChevronRightIcon />}
          onClick={() => goToPage(safeCurrentPage + 1)}
          isDisabled={safeCurrentPage >= totalPages}
          {...navBtnStyles}
        />
      </HStack>
    </Box>
  );
};

export default AppPagination;
